/**
 * Stretch for type: the glyphs keep their width and are drawn shorter or taller.
 * Stored as a factor of normal height (1 = untouched, 0.5 = squished, 2 = stretched).
 * Values above STRETCH_PERCENT_FROM were saved as a percentage (95 = 0.95).
 */

export const SQUISH_LIMITS = { min: 0.5, max: 2, step: 0.01 };

const STRETCH_PERCENT_FROM = 2.5;

/** @returns {number|null} A factor within the limits, or null when unset */
export function cleanSquish(value) {
    if (value == null || value === '') return null;
    let number = Number(value);
    if (!Number.isFinite(number)) return null;
    if (number > STRETCH_PERCENT_FROM) number /= 100;
    const clamped = Math.min(SQUISH_LIMITS.max, Math.max(SQUISH_LIMITS.min, number));
    return Math.round(clamped * 100) / 100;
}

/** CSS `transform` for a stretch, or null when unset. */
export function squishTransform(value) {
    const squish = cleanSquish(value);
    if (squish == null) return null;
    return squish === 1 ? 'none' : `scaleY(${squish})`;
}

/** CSS `scale` for a stretch, or undefined when unset. */
export function squishScale(value) {
    const squish = cleanSquish(value);
    return squish == null ? undefined : `1 ${squish}`;
}

/** The factor in a `scaleY(…)` transform, or null. */
export function squishFromTransform(transform) {
    if (String(transform || '').trim() === 'none') return 1;
    const match = String(transform || '').match(/^\s*scaleY\(\s*(\d*\.?\d+)\s*\)\s*$/);
    return match ? cleanSquish(match[1]) : null;
}

/** A Type control metric for stretch. */
export function squishMetric(value, extra = {}) {
    return {
        key: 'squish',
        label: 'Stretch',
        title: 'Letter height: 1 is normal, below 1 squishes, above 1 stretches',
        kind: 'number',
        value: cleanSquish(value),
        ...SQUISH_LIMITS,
        suffix: '×',
        allowEmpty: true,
        placeholder: '1',
        ...extra,
    };
}
